package com.schoolconnect.app.feature.home

import com.schoolconnect.app.datasource.network.AuthSessionStore
import com.schoolconnect.app.datasource.NavigationStateStore
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.common.shellRoleSubtitle
import com.schoolconnect.app.feature.common.ShellProfileStore
import com.schoolconnect.app.feature.rolepicker.RolePickerOption
import com.schoolconnect.app.feature.rolepicker.RolePickerUiState
import com.schoolconnect.app.feature.home.HomeUserProfileUiState
import com.schoolconnect.app.datasource.network.response.UserRoleAuthorization
import com.schoolconnect.app.datasource.network.response.UserRoleAuthorizationsResponse
import com.schoolconnect.app.datasource.network.response.TeacherDashboardOverviewResponse
import com.schoolconnect.app.datasource.network.response.ParentResponse
import com.schoolconnect.app.datasource.network.response.SchoolStaffResponse
import com.schoolconnect.app.datasource.network.response.AppStaffResponse
import com.schoolconnect.app.datasource.network.response.resolveRoleCode
import com.schoolconnect.app.repository.academicyear.AcademicYearRepository
import com.schoolconnect.app.repository.analytics.SchoolStatisticsRepository
import com.schoolconnect.app.repository.home.HomeDashboardRepository
import com.schoolconnect.app.repository.school.SchoolRepository
import com.schoolconnect.app.repository.schoolstaff.SchoolStaffRepository
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.repository.profile.ProfileRepository
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.repository.common.TenantContextCacheCoordinator
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.atomicfu.AtomicInt
import kotlinx.atomicfu.atomic
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

class HomeViewModel(
    homeRoleCode: String = "",
    homeSchoolLabel: String = "iConnect.School",
    homeSchoolId: String = "",
    homeSchoolCode: String = "",
    initialSelectedNavId: String = "dashboard",
    private val onHomeRoleChanged: (String, String, String) -> Unit = { _, _, _ -> }
) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] HomeViewModel"
    }

    private val scope = CoroutineScope(
        SupervisorJob() + Dispatchers.Default + CoroutineExceptionHandler { _, throwable ->
            // Optional dashboard requests must never crash the authenticated shell.
            scLog("$TAG unhandled dashboard request failure; keeping shell usable", throwable)
        }
    )
    private val dashboardMetricsRequestsInFlight = atomic(0)
    private val dashboardFeedRequestsInFlight = atomic(0)
    private var rolePickerRequestInFlight = false
    private var userProfileRequestInFlight = false
    private val normalizedRoleCode = homeRoleCode.takeIf { it.isNotBlank() } ?: SchoolKeys.V_ROLE_APP_SUPER_ADMIN
    private val normalizedSchoolId = homeSchoolId.trim().ifBlank { UserSessionStore.getHomeRoleSchoolId().trim() }
    private val normalizedSchoolCode = homeSchoolCode.trim()
        .ifBlank { UserSessionStore.getHomeRoleSchoolCode().trim() }
    private val cachedDashboardAnalytics = HomeDashboardRepository.peekDashboardAnalytics(normalizedRoleCode)
    private val cachedDashboardSchoolsAnalytics = HomeDashboardRepository.peekDashboardSchoolsAnalytics(normalizedRoleCode)
    private val cachedDashboardStatistics = HomeDashboardRepository.peekDashboardStatistics(normalizedRoleCode)
    private val cachedSchoolAnalytics = HomeDashboardRepository.peekSchoolAnalytics(normalizedSchoolCode)
    private val cachedSchoolStatistics = SchoolStatisticsRepository.peekSchoolStatistics(normalizedSchoolId)
    private val cachedSchoolEventsNotices = HomeDashboardRepository.peekSchoolEventsNotices(normalizedSchoolId)
    private val cachedRecentActivities = HomeDashboardRepository.peekRecentActivities(normalizedRoleCode)
    private val cachedSystemHealth = HomeDashboardRepository.peekSystemHealth(normalizedRoleCode)
    private val cachedTeacherDashboard = HomeDashboardRepository.peekTeacherDashboard(normalizedRoleCode)
    private val authenticatedUserId = resolveCurrentUserId()
    private val cachedParentDashboard = HomeDashboardRepository.peekParentDashboard("$authenticatedUserId:$normalizedSchoolId")
    private val cachedParentWards = HomeDashboardRepository.peekParentWards(cachedParentDashboard?.parentId.orEmpty()).orEmpty()
    private val cachedActiveAnnouncements = HomeDashboardRepository.peekActiveAnnouncements(normalizedSchoolId).orEmpty()
    private val hasCachedSchoolDashboardData =
        supportsSchoolDashboard(normalizedRoleCode) && cachedSchoolAnalytics != null
    private val hasCachedSchoolStatisticsData =
        supportsSchoolDashboard(normalizedRoleCode) && normalizedSchoolId.isNotBlank() && cachedSchoolStatistics != null
    private val hasCachedSchoolFeedData =
        supportsSchoolDashboard(normalizedRoleCode) && normalizedSchoolId.isNotBlank() && cachedSchoolEventsNotices != null
    private val _uiState = MutableStateFlow(
        HomeUiState(
            roleCode = normalizedRoleCode,
            roleLabel = shellRoleLabel(normalizedRoleCode),
            roleSubtitle = shellRoleSubtitle(normalizedRoleCode),
            schoolLabel = homeSchoolLabel,
            schoolId = normalizedSchoolId,
            schoolCode = normalizedSchoolCode,
            isSidebarCollapsed = NavigationStateStore.getSidebarCollapsed(
                userId = resolveCurrentUserId(),
                roleCode = normalizedRoleCode,
                schoolId = normalizedSchoolId
            ),
            isSchoolLogoLoading = normalizedRoleCode == SchoolKeys.V_ROLE_SCHOOL_ADMIN && normalizedSchoolCode.isNotBlank(),
            isActiveAcademicYearLoading = supportsSchoolDashboard(normalizedRoleCode) &&
                (normalizedSchoolId.isNotBlank() || normalizedSchoolCode.isNotBlank()),
            isSchoolStatisticsLoading = supportsSchoolDashboard(normalizedRoleCode) &&
                normalizedSchoolId.isNotBlank() &&
                !hasCachedSchoolStatisticsData,
            selectedNavId = initialSelectedNavId,
            // Individual loaders below own these flags. Keeping the initial values false
            // prevents a missing optional cache entry from creating a permanent shimmer.
            isDashboardLoading = false,
            isDashboardFeedLoading = false,
            isSchoolDashboardLoading = supportsSchoolDashboard(normalizedRoleCode) && !hasCachedSchoolDashboardData,
            isSchoolDashboardEventsNoticesLoading = supportsSchoolDashboard(normalizedRoleCode) && !hasCachedSchoolFeedData,
            dashboardAnalytics = cachedDashboardAnalytics,
            dashboardSchoolsAnalytics = cachedDashboardSchoolsAnalytics,
            dashboardStatistics = cachedDashboardStatistics,
            schoolDashboardAnalytics = cachedSchoolAnalytics,
            schoolStatistics = cachedSchoolStatistics,
            schoolDashboardEventsNotices = cachedSchoolEventsNotices,
            recentActivities = cachedRecentActivities,
            systemHealth = cachedSystemHealth,
            teacherDashboard = cachedTeacherDashboard,
            isTeacherDashboardLoading = normalizedRoleCode == SchoolKeys.V_ROLE_TEACHER &&
                initialSelectedNavId == "dashboard" &&
                cachedTeacherDashboard == null,
            parentDashboard = cachedParentDashboard,
            parentProfile = null,
            parentWards = cachedParentWards,
            isParentDashboardLoading = normalizedRoleCode == SchoolKeys.V_ROLE_PARENT && cachedParentDashboard == null,
            isParentProfileLoading = normalizedRoleCode == SchoolKeys.V_ROLE_PARENT,
            activeAnnouncements = cachedActiveAnnouncements,
            isActiveAnnouncementsLoading = normalizedRoleCode == SchoolKeys.V_ROLE_PARENT && cachedActiveAnnouncements.isEmpty(),
            userProfile = HomeUserProfileUiState(isLoading = true),
            rolePicker = HomeDefaults.createRolePickerState()
        )
    )

    val uiState: StateFlow<HomeUiState> = _uiState.asStateFlow()

    init {
        if (normalizedRoleCode == SchoolKeys.V_ROLE_PARENT) {
            loadParentHomeIfNeeded()
            loadCurrentUserProfileIfNeeded()
            loadActiveAnnouncementsIfNeeded(normalizedSchoolId)
        } else {
            loadDashboardAnalyticsIfNeeded(normalizedRoleCode)
            loadSchoolsAnalyticsIfNeeded(normalizedRoleCode)
            loadDashboardStatisticsIfNeeded(normalizedRoleCode)
            loadRecentActivitiesIfNeeded(normalizedRoleCode)
            // Health is non-critical; load after metrics so it doesn't compete on first paint.
            deferSystemHealthLoad(normalizedRoleCode)
            if (initialSelectedNavId == "dashboard") {
                loadTeacherDashboardIfNeeded(normalizedRoleCode)
            }
            loadSchoolAnalyticsIfNeeded(normalizedRoleCode, normalizedSchoolId, normalizedSchoolCode)
            loadSchoolStatisticsIfNeeded(normalizedRoleCode, normalizedSchoolId)
            loadSchoolActiveAcademicYearIfNeeded(normalizedRoleCode, normalizedSchoolId, normalizedSchoolCode)
            loadSchoolEventsNoticesIfNeeded(normalizedRoleCode, normalizedSchoolId)
            loadCurrentUserProfileIfNeeded()
            loadRolePickerAuthorizationsIfNeeded()
            loadSchoolLogoIfNeeded()
        }
    }

    private fun loadActiveAnnouncementsIfNeeded(schoolId: String, forceRemote: Boolean = false) {
        if (_uiState.value.roleCode != SchoolKeys.V_ROLE_PARENT) return
        scope.launch {
            HomeDashboardRepository.loadActiveAnnouncements(schoolId, forceRemote = forceRemote).onSuccess { announcements ->
                _uiState.update {
                    it.copy(activeAnnouncements = announcements, isActiveAnnouncementsLoading = false)
                }
            }.onFailure { throwable ->
                scLog("$TAG loadActiveAnnouncements failure school=${schoolId.ifBlank { "<blank>" }}", throwable)
                _uiState.update { it.copy(isActiveAnnouncementsLoading = false) }
            }
        }
    }

    private fun loadSchoolLogoIfNeeded() {
        if (normalizedRoleCode != SchoolKeys.V_ROLE_SCHOOL_ADMIN || normalizedSchoolCode.isBlank()) {
            _uiState.update { it.copy(isSchoolLogoLoading = false) }
            return
        }
        scope.launch {
            SchoolRepository.loadSchoolByCode(normalizedSchoolCode).fold(
                onSuccess = { school ->
                    val logoUrl = school.schoolLogoUrl.orEmpty().trim()
                    if (logoUrl.isBlank()) {
                        _uiState.update { it.copy(isSchoolLogoLoading = false) }
                    } else {
                        _uiState.update { it.copy(schoolLogoUrl = logoUrl) }
                        FileUploadRepository.downloadBytes(logoUrl).fold(
                            onSuccess = { bytes -> _uiState.update { it.copy(schoolLogoBytes = bytes, isSchoolLogoLoading = false) } },
                            onFailure = { _uiState.update { it.copy(isSchoolLogoLoading = false) } }
                        )
                    }
                },
                onFailure = { _uiState.update { it.copy(isSchoolLogoLoading = false) } }
            )
        }
    }

    fun onEvent(event: HomeUiEvent) {
        when (event) {
            HomeUiEvent.AccountMenuToggled -> _uiState.update {
                it.copy(isAccountMenuExpanded = !it.isAccountMenuExpanded)
            }
            HomeUiEvent.AccountMenuDismissed -> _uiState.update {
                it.copy(isAccountMenuExpanded = false)
            }
            HomeUiEvent.RolePickerRequested -> {
                val shouldLoad = _uiState.value.rolePicker.options.isEmpty() && !rolePickerRequestInFlight
                _uiState.update {
                    val selectedId = it.rolePicker.selectedOptionId.ifBlank {
                        it.rolePicker.options.firstOrNull()?.id.orEmpty()
                    }
                    it.copy(
                        isAccountMenuExpanded = false,
                        rolePicker = it.rolePicker.copy(
                            isVisible = true,
                            selectedOptionId = selectedId
                        )
                    )
                }
                if (shouldLoad) {
                    loadRolePickerAuthorizationsIfNeeded()
                }
            }
            is HomeUiEvent.NavSelected -> {
                val normalizedNavId = event.navId.trim().ifBlank { "dashboard" }
                val navChanged = _uiState.value.selectedNavId != normalizedNavId
                _uiState.update {
                    it.copy(
                        selectedNavId = normalizedNavId,
                        // A repeated click on the active rail item should not
                        // recreate its ViewModel and restart all of its loads.
                        // Changing items still advances the key for the
                        // feature screens that use it as a scope boundary.
                        railActivationKey = if (navChanged) {
                            it.railActivationKey + 1L
                        } else {
                            it.railActivationKey
                        },
                        isAccountMenuExpanded = false
                    )
                }
                NavigationStateStore.setLastRailId(
                    userId = resolveCurrentUserId(),
                    roleCode = _uiState.value.roleCode,
                    schoolId = _uiState.value.schoolId,
                    railId = normalizedNavId
                )
                if (navChanged && normalizedNavId == "dashboard") {
                    refreshDashboard(_uiState.value.roleCode)
                }
            }
            HomeUiEvent.SidebarCollapseToggled -> {
                val collapsed = !_uiState.value.isSidebarCollapsed
                _uiState.update { it.copy(isSidebarCollapsed = collapsed) }
                NavigationStateStore.setSidebarCollapsed(
                    userId = resolveCurrentUserId(),
                    roleCode = _uiState.value.roleCode,
                    schoolId = _uiState.value.schoolId,
                    collapsed = collapsed
                )
            }
            is HomeUiEvent.RolePickerSelectionChanged -> {
                _uiState.update {
                    it.copy(
                        rolePicker = it.rolePicker.copy(
                            selectedOptionId = event.optionId,
                            isSubmitting = false
                        )
                    )
                }
            }
            is HomeUiEvent.RolePickerRememberChanged -> _uiState.update {
                it.copy(
                    rolePicker = it.rolePicker.copy(rememberSelection = event.value)
                )
            }
            HomeUiEvent.RolePickerClosed -> _uiState.update {
                it.copy(
                    isAccountMenuExpanded = false,
                    rolePicker = it.rolePicker.copy(isVisible = false, isLoading = false, isSubmitting = false)
                )
            }
            HomeUiEvent.RolePickerContinueClicked -> confirmRoleSelection()
            is HomeUiEvent.ProfileSaved -> saveProfile(event.values)
        }
    }

    private fun saveProfile(values: EditProfileFormValues) {
        val fullName = values.fullName.trim().ifBlank {
            listOf(values.firstName, values.middleName, values.lastName)
                .map(String::trim)
                .filter(String::isNotBlank)
                .joinToString(" ")
        }
        scope.launch {
            ProfileRepository.updateCurrentProfile(
                _uiState.value.roleCode,
                ProfileRepository.ProfileUpdate(
                    fullName = fullName,
                    pushNotificationsEnabled = values.pushNotificationsEnabled,
                    emailNotificationsEnabled = values.emailNotificationsEnabled,
                    photoFileId = values.selectedPhotoFileId
                )
            ).fold(
                onSuccess = {
                    AppToastCenter.showSuccess("Profile updated successfully")
                    loadCurrentUserProfileIfNeeded()
                },
                onFailure = { throwable -> scLog("$TAG saveProfile failure role=${_uiState.value.roleCode}", throwable) }
            )
        }
    }

    private fun confirmRoleSelection() {
        val state = _uiState.value
        if (state.rolePicker.isSubmitting) return
        val selectedOption = state.rolePicker.options.firstOrNull { it.id == state.rolePicker.selectedOptionId }
            ?: state.rolePicker.options.firstOrNull()
            ?: return
        _uiState.update { current ->
            current.copy(
                // Close the dialog immediately. The role-context request will
                // show the app-level loading overlay while Home refreshes.
                rolePicker = current.rolePicker.copy(
                    isVisible = false,
                    isSubmitting = true
                )
            )
        }
        updateSelectedRoleContext(selectedOption.id)
    }

    private fun applySelectedRoleContext(selectedOption: RolePickerOption) {
        UserSessionStore.setActiveAssignmentId(selectedOption.id)
        val nextDashboardAnalytics = HomeDashboardRepository.peekDashboardAnalytics(selectedOption.roleCode)
        val nextDashboardSchoolsAnalytics = HomeDashboardRepository.peekDashboardSchoolsAnalytics(selectedOption.roleCode)
        val nextDashboardStatistics = HomeDashboardRepository.peekDashboardStatistics(selectedOption.roleCode)
        val nextSchoolCode = selectedOption.schoolCode.trim()
        val nextSchoolId = selectedOption.schoolId.trim()
        val nextSchoolAnalytics = HomeDashboardRepository.peekSchoolAnalytics(nextSchoolCode)
        val nextSchoolEventsNotices = HomeDashboardRepository.peekSchoolEventsNotices(nextSchoolId)
        val nextSchoolStatistics = SchoolStatisticsRepository.peekSchoolStatistics(nextSchoolId)
        val nextRecentActivities = HomeDashboardRepository.peekRecentActivities(selectedOption.roleCode)
        val nextSystemHealth = HomeDashboardRepository.peekSystemHealth(selectedOption.roleCode)
        val nextTeacherDashboard = HomeDashboardRepository.peekTeacherDashboard(selectedOption.roleCode)
        UserSessionStore.setHomeRoleSchoolCode(nextSchoolCode)
        dashboardMetricsRequestsInFlight.value = 0
        dashboardFeedRequestsInFlight.value = 0
        _uiState.update {
            it.copy(
                isAccountMenuExpanded = false,
                roleCode = selectedOption.roleCode,
                roleLabel = shellRoleLabel(selectedOption.roleCode),
                roleSubtitle = shellRoleSubtitle(selectedOption.roleCode),
                schoolLabel = selectedOption.schoolLabel,
                schoolId = nextSchoolId,
                schoolCode = nextSchoolCode,
                selectedNavId = "dashboard",
                isSidebarCollapsed = false,
                // Clear prior role staff fields until the new /me profile loads.
                userProfile = it.userProfile.copy(
                    employeeId = "",
                    schoolRole = "",
                    authRole = "",
                    isLoading = true
                ),
                // The request methods below set these flags from their active request counts.
                isDashboardLoading = false,
                isDashboardFeedLoading = false,
                isSchoolDashboardLoading = supportsSchoolDashboard(selectedOption.roleCode) && nextSchoolAnalytics == null,
                isActiveAcademicYearLoading = supportsSchoolDashboard(selectedOption.roleCode) &&
                    (nextSchoolId.isNotBlank() || nextSchoolCode.isNotBlank()),
                isSchoolStatisticsLoading = supportsSchoolDashboard(selectedOption.roleCode) &&
                    nextSchoolId.isNotBlank() &&
                    nextSchoolStatistics == null,
                isSchoolDashboardEventsNoticesLoading = supportsSchoolDashboard(selectedOption.roleCode) &&
                    nextSchoolId.isNotBlank() &&
                    nextSchoolEventsNotices == null,
                dashboardAnalytics = nextDashboardAnalytics,
                dashboardSchoolsAnalytics = nextDashboardSchoolsAnalytics,
                dashboardStatistics = nextDashboardStatistics,
                schoolDashboardAnalytics = nextSchoolAnalytics,
                schoolStatistics = nextSchoolStatistics,
                schoolDashboardEventsNotices = nextSchoolEventsNotices,
                recentActivities = nextRecentActivities,
                systemHealth = nextSystemHealth,
                teacherDashboard = nextTeacherDashboard,
                isTeacherDashboardLoading = selectedOption.roleCode == SchoolKeys.V_ROLE_TEACHER && nextTeacherDashboard == null,
                rolePicker = it.rolePicker.copy(isVisible = false, isSubmitting = false)
            )
        }
        onHomeRoleChanged(selectedOption.roleCode, selectedOption.schoolId, selectedOption.schoolLabel)
        loadDashboardAnalyticsIfNeeded(selectedOption.roleCode)
        loadSchoolsAnalyticsIfNeeded(selectedOption.roleCode)
        loadDashboardStatisticsIfNeeded(selectedOption.roleCode)
        loadRecentActivitiesIfNeeded(selectedOption.roleCode)
        deferSystemHealthLoad(selectedOption.roleCode)
        loadSchoolAnalyticsIfNeeded(selectedOption.roleCode, selectedOption.schoolId, nextSchoolCode)
        loadSchoolStatisticsIfNeeded(selectedOption.roleCode, selectedOption.schoolId)
        loadSchoolActiveAcademicYearIfNeeded(selectedOption.roleCode, selectedOption.schoolId, nextSchoolCode)
        loadSchoolEventsNoticesIfNeeded(selectedOption.roleCode, nextSchoolId)
        loadTeacherDashboardIfNeeded(selectedOption.roleCode)
    }

    private fun updateSelectedRoleContext(optionId: String) {
        val state = _uiState.value
        val selectedOption = state.rolePicker.options.firstOrNull { it.id == optionId } ?: run {
            _uiState.update { current ->
                current.copy(
                    rolePicker = current.rolePicker.copy(isSubmitting = false)
                )
            }
            return
        }

        scope.launch {
            scLog(
                "$TAG updateSelectedRoleContext start assignmentId=${selectedOption.id} role=${selectedOption.roleCode} school=${selectedOption.schoolId.ifBlank { "<blank>" }} code=${selectedOption.schoolCode.ifBlank { "<blank>" }}"
            )
            try {
                val result = UserRepository.updateUserRoleSelection(selectedOption.id)
                result.fold(
                    onSuccess = {
                        TenantContextCacheCoordinator.invalidateAll("role-selection")
                        _uiState.update { current ->
                            val refreshedOptions = current.rolePicker.options.map { option ->
                                option.copy(isLastUsed = option.id == selectedOption.id)
                            }
                            current.copy(
                                rolePicker = current.rolePicker.copy(
                                    options = refreshedOptions,
                                    isSubmitting = false
                                )
                            )
                        }
                        applySelectedRoleContext(selectedOption)
                        scLog("$TAG updateSelectedRoleContext success assignmentId=${selectedOption.id}")
                    },
                    onFailure = { throwable ->
                        scLog("$TAG updateSelectedRoleContext failure assignmentId=${selectedOption.id}", throwable)
                        _uiState.update { current ->
                            current.copy(
                                rolePicker = current.rolePicker.copy(
                                    isSubmitting = false
                                )
                            )
                        }
                    }
                )
            } catch (throwable: Throwable) {
                scLog("$TAG updateSelectedRoleContext error assignmentId=${selectedOption.id}", throwable)
                _uiState.update { current ->
                    current.copy(
                        rolePicker = current.rolePicker.copy(
                            isSubmitting = false
                        )
                    )
                }
            }
        }
    }

    private fun loadRolePickerAuthorizationsIfNeeded() {
        if (rolePickerRequestInFlight) return
        val userId = resolveCurrentUserId()
        if (userId.isBlank()) {
            scLog("$TAG loadRolePickerAuthorizations skipped user=<blank>")
            _uiState.update { current ->
                current.copy(
                    rolePicker = current.rolePicker.copy(
                        isLoading = false,
                        errorMessage = "No authenticated user id was available."
                    )
                )
            }
            return
        }

        rolePickerRequestInFlight = true
        _uiState.update { current ->
            current.copy(
                rolePicker = current.rolePicker.copy(
                    isLoading = true,
                    errorMessage = if (current.rolePicker.options.isEmpty()) "" else current.rolePicker.errorMessage
                )
            )
        }

        scope.launch {
            scLog("$TAG loadRolePickerAuthorizations start user=$userId")
            try {
                val result = UserRepository.loadUserRoleAuthorizations(userId)
                result.fold(
                    onSuccess = { response ->
                        val options = response.toRolePickerOptions()
                        val activeAssignmentId = UserSessionStore.getActiveAssignmentId().trim()
                        val authorizations = response.authorizations.orEmpty()
                        val matchingAuthorizations = authorizations.filter {
                            mapRoleCode(it.roleName.orEmpty(), it.roleId ?: -1) == normalizedRoleCode &&
                                it.tenant?.schoolId.orEmpty().trim() == normalizedSchoolId
                        }
                        val activeAuthorization = authorizations.firstOrNull {
                            activeAssignmentId.isNotBlank() &&
                                it.assignmentId.orEmpty().trim() == activeAssignmentId &&
                                mapRoleCode(it.roleName.orEmpty(), it.roleId ?: -1) == normalizedRoleCode &&
                                (it.tenant?.schoolId.orEmpty().trim().isBlank() ||
                                    it.tenant?.schoolId.orEmpty().trim() == normalizedSchoolId)
                        } ?: matchingAuthorizations.firstOrNull()
                        val authorizedSchoolName = activeAuthorization?.tenant?.schoolName
                            ?.trim()
                            ?.takeIf(String::isNotBlank)
                        val lastLoginLabel = response.resolveLastUsedLabel(normalizedRoleCode)
                        val selectedOptionId = options.firstOrNull { it.roleCode == normalizedRoleCode }?.id
                            ?: options.firstOrNull { it.isLastUsed }?.id
                            ?: options.firstOrNull()?.id.orEmpty()
                        _uiState.update { current ->
                            current.copy(
                                schoolLabel = authorizedSchoolName ?: current.schoolLabel,
                                userProfile = current.userProfile.copy(
                                    lastLoginLabel = lastLoginLabel.ifBlank { current.userProfile.lastLoginLabel }
                                ),
                                rolePicker = current.rolePicker.copy(
                                    title = "Change Role",
                                    subtitle = "Select a role to continue",
                                    options = options,
                                    selectedOptionId = selectedOptionId,
                                    isVisible = current.rolePicker.isVisible,
                                    isLoading = false,
                                    errorMessage = "",
                                    isSubmitting = false
                                )
                            )
                        }
                        scLog(
                            "$TAG loadRolePickerAuthorizations success user=${response.userId.orEmpty().ifBlank { userId }} activeProfiles=${response.activeProfilesCount ?: 0} options=${options.size}"
                        )
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadRolePickerAuthorizations failure user=$userId", throwable)
                        _uiState.update { current ->
                            current.copy(
                                rolePicker = current.rolePicker.copy(
                                    isLoading = false,
                                    errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load role authorizations." }
                                )
                            )
                        }
                    }
                )
            } finally {
                rolePickerRequestInFlight = false
            }
        }
    }

    private fun loadCurrentUserProfileIfNeeded() {
        if (userProfileRequestInFlight) return
        userProfileRequestInFlight = true
        _uiState.update { current -> current.copy(userProfile = current.userProfile.copy(isLoading = true)) }

        scope.launch {
            try {
                scLog("$TAG loadCurrentUserProfile start role=$normalizedRoleCode school=$normalizedSchoolId")
                val result = when {
                    normalizedRoleCode == SchoolKeys.V_ROLE_PARENT ->
                        ProfileRepository.loadParentProfile().map { it.toHomeUserProfile() }
                    normalizedRoleCode == SchoolKeys.V_ROLE_SCHOOL_ADMIN ||
                        normalizedRoleCode == SchoolKeys.V_ROLE_TEACHER ||
                        normalizedRoleCode == SchoolKeys.V_ROLE_SUPPORT_STAFF ->
                        ProfileRepository.loadSchoolStaffProfile().map { it.toHomeUserProfile() }
                    normalizedRoleCode == SchoolKeys.V_ROLE_APP_SUPER_ADMIN ->
                        // Super Admin is an app-staff profile. Refresh from the
                        // app-staff endpoint so profile_image_url and notification
                        // preferences are read from the same resource that is
                        // updated by ProfileRepository.
                        ProfileRepository.loadAppStaffProfile().map { it.toHomeUserProfile() }
                    else -> ProfileRepository.loadAppStaffProfile().map { it.toHomeUserProfile() }
                }
                result.fold(
                    onSuccess = { profile ->
                        ShellProfileStore.update(
                            fullName = profile.fullName,
                            email = profile.email,
                            profileImageUrl = profile.profileImageUrl,
                            pushNotificationsEnabled = profile.pushNotificationsEnabled,
                            emailNotificationsEnabled = profile.emailNotificationsEnabled,
                            mobileNumber = profile.mobileNumber,
                            employeeId = profile.employeeId,
                            schoolRole = profile.schoolRole,
                            authRole = profile.authRole,
                            mobileVerified = profile.mobileVerified,
                            emailVerified = profile.emailVerified
                        )
                        _uiState.update { current ->
                            current.copy(
                                userProfile = profile.copy(
                                    lastLoginLabel = current.userProfile.lastLoginLabel.ifBlank { profile.lastLoginLabel }
                                )
                            )
                        }
                        scLog("$TAG loadCurrentUserProfile success name=${profile.fullName.ifBlank { "<blank>" }}")
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadCurrentUserProfile failure role=$normalizedRoleCode", throwable)
                        _uiState.update { current ->
                            current.copy(
                                userProfile = current.userProfile.copy(isLoading = false)
                            )
                        }
                    }
                )
            } finally {
                userProfileRequestInFlight = false
            }
        }
    }

    private fun AppStaffResponse.toHomeUserProfile(): HomeUserProfileUiState {
        val fullName = name.orEmpty().trim()
        val parts = fullName.split(Regex("\\s+")).filter { it.isNotBlank() }
        val custom = customAttributes.orEmpty()
        fun customString(vararg keys: String): String = keys.firstNotNullOfOrNull { key ->
            custom[key]?.jsonPrimitive?.contentOrNull?.trim()?.takeIf { it.isNotBlank() }
        }.orEmpty()
        return HomeUserProfileUiState(
            fullName = fullName,
            firstName = customString("first_name", "firstName").ifBlank { parts.firstOrNull().orEmpty() },
            middleName = customString("middle_name", "middleName"),
            lastName = customString("last_name", "lastName").ifBlank { parts.drop(1).joinToString(" ") },
            mobileNumber = mobile.orEmpty().ifBlank { customString("mobile", "mobile_number", "phone") },
            email = email.orEmpty().ifBlank { customString("email", "email_address", "mail") },
            employeeId = employeeId.orEmpty(),
            mobileVerified = custom["mobile_verified"]?.jsonPrimitive?.booleanOrNull,
            emailVerified = custom["email_verified"]?.jsonPrimitive?.booleanOrNull,
            profileImageUrl = profileImageUrl.orEmpty(),
            pushNotificationsEnabled = pushNotificationEnabled ?: true,
            emailNotificationsEnabled = emailNotificationEnabled ?: true,
            isLoading = false
        )
    }

    private fun loadTeacherProfileIfNeeded() {
        if (userProfileRequestInFlight) return
        val userId = resolveCurrentUserId()
        val staffId = AuthSessionStore.getSession()?.response?.identityContextProfiles.orEmpty()
            .firstOrNull { profile ->
                profile.resolveRoleCode() == SchoolKeys.V_ROLE_TEACHER &&
                    profile.schoolId.orEmpty().trim() == normalizedSchoolId
            }?.staffId.orEmpty().trim()
            .ifBlank { UserSessionStore.getActiveAssignmentId().trim() }
            .ifBlank { AuthSessionStore.primaryContext()?.assignmentId.orEmpty().trim() }
        if (normalizedSchoolId.isBlank() || userId.isBlank()) {
            scLog("$TAG loadTeacherProfile skipped school=${normalizedSchoolId.ifBlank { "<blank>" }} user=${userId.ifBlank { "<blank>" }}")
            _uiState.update { current ->
                current.copy(userProfile = current.userProfile.copy(isLoading = false))
            }
            return
        }

        userProfileRequestInFlight = true
        _uiState.update { current -> current.copy(userProfile = current.userProfile.copy(isLoading = true)) }
        scope.launch {
            scLog("$TAG loadTeacherProfile start school=$normalizedSchoolId user=$userId")
            try {
                SchoolStaffRepository.loadTeacherSchoolStaffProfile(
                    schoolId = normalizedSchoolId,
                    userId = userId,
                    preferredStaffId = staffId
                ).fold(
                    onSuccess = { response ->
                        val profile = response.toHomeUserProfile()
                        ShellProfileStore.update(
                            fullName = profile.fullName,
                            email = profile.email,
                            profileImageUrl = profile.profileImageUrl,
                            pushNotificationsEnabled = profile.pushNotificationsEnabled,
                            emailNotificationsEnabled = profile.emailNotificationsEnabled,
                            mobileNumber = profile.mobileNumber,
                            employeeId = profile.employeeId,
                            schoolRole = profile.schoolRole,
                            authRole = profile.authRole,
                            mobileVerified = profile.mobileVerified,
                            emailVerified = profile.emailVerified
                        )
                        _uiState.update { current ->
                            current.copy(
                                userProfile = profile.copy(
                                    lastLoginLabel = current.userProfile.lastLoginLabel.ifBlank { profile.lastLoginLabel }
                                )
                            )
                        }
                        scLog(
                            "$TAG loadTeacherProfile success staffId=${response.id.orEmpty()} name=${profile.fullName.ifBlank { "<blank>" }} email=${profile.email.ifBlank { "<blank>" }}"
                        )
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadTeacherProfile failure school=$normalizedSchoolId user=$userId", throwable)
                        _uiState.update { current -> current.copy(userProfile = current.userProfile.copy(isLoading = false)) }
                    }
                )
            } finally {
                userProfileRequestInFlight = false
            }
        }
    }

    private fun resolveCurrentUserId(): String {
        return UserSessionStore.getUserId()
            .ifBlank { AuthSessionStore.getSession()?.response?.userId.orEmpty() }
            .ifBlank { AuthSessionStore.primaryContext()?.userId.orEmpty() }
    }

    private fun UserRoleAuthorizationsResponse.resolveLastUsedLabel(roleCode: String): String {
        val authorization = authorizations.orEmpty().firstOrNull { mapRoleCode(it.roleName.orEmpty(), it.roleId ?: -1) == roleCode }
            ?: authorizations.orEmpty().firstOrNull()
        return authorization?.lastUsedAt
            ?.takeIf { it.isNotBlank() }
            ?.toCompactTimestampLabel()
            .orEmpty()
    }

    private fun UserRoleAuthorizationsResponse.toRolePickerOptions(): List<RolePickerOption> {
        val latestAssignmentId = authorizations.orEmpty().firstOrNull()?.assignmentId.orEmpty()
        return authorizations.orEmpty().mapIndexed { index, authorization ->
            authorization.toRolePickerOption(
                index = index,
                selectedRoleCode = normalizedRoleCode,
                isLastUsed = authorization.assignmentId.orEmpty() == latestAssignmentId || index == 0
            )
        }
    }

    private fun UserRoleAuthorization.toRolePickerOption(
        index: Int,
        selectedRoleCode: String,
        isLastUsed: Boolean
    ): RolePickerOption {
        val schoolIdValue = tenant?.schoolId.orEmpty()
        val schoolLabel = tenant?.schoolName.orEmpty().ifBlank {
            tenant?.schoolCode.orEmpty().ifBlank {
                if (schoolIdValue.isBlank()) "Platform Context" else "School Context"
            }
        }
        val roleCode = mapRoleCode(roleName.orEmpty(), roleId ?: -1)
        // Backend authorization names are not presentation labels. Use the
        // canonical role label so the header, avatar, and profile card stay
        // consistent after switching roles.
        val roleLabel = shellRoleLabel(roleCode)
        return RolePickerOption(
            id = assignmentId.orEmpty().ifBlank { "authorization-$index" },
            roleCode = roleCode,
            schoolId = schoolIdValue,
            roleLabel = roleLabel,
            schoolLabel = schoolLabel,
            isLastUsed = isLastUsed || roleCode == selectedRoleCode
        )
    }

    private fun mapRoleCode(roleName: String, roleId: Int): String {
        return when (roleName.trim().uppercase()) {
            "SUPER_ADMIN" -> SchoolKeys.V_ROLE_APP_SUPER_ADMIN
            "APP_ADMIN" -> SchoolKeys.V_ROLE_APP_ADMIN
            "BACKEND" -> SchoolKeys.V_ROLE_APP_BACKEND
            "SCHOOL_ADMIN", "SCHOOL_MGMT" -> SchoolKeys.V_ROLE_SCHOOL_ADMIN
            "TEACHER", "SCHOOL_TEACHER" -> SchoolKeys.V_ROLE_TEACHER
            "PARENT" -> SchoolKeys.V_ROLE_PARENT
            "SUPPORT_STAFF", "SCHOOL_SUPPORT_STAFF" -> SchoolKeys.V_ROLE_SUPPORT_STAFF
            else -> when (roleId) {
                1 -> SchoolKeys.V_ROLE_APP_SUPER_ADMIN
                2 -> SchoolKeys.V_ROLE_APP_ADMIN
                3 -> SchoolKeys.V_ROLE_APP_BACKEND
                4 -> SchoolKeys.V_ROLE_SCHOOL_ADMIN
                5 -> SchoolKeys.V_ROLE_SCHOOL_ADMIN
                6 -> SchoolKeys.V_ROLE_TEACHER
                7 -> SchoolKeys.V_ROLE_PARENT
                8 -> SchoolKeys.V_ROLE_SUPPORT_STAFF
                else -> normalizedRoleCode
            }
        }
    }

    private fun String.toDisplayRoleLabel(): String {
        val normalized = trim().replace('_', ' ').lowercase()
        return normalized.replaceFirstChar { char ->
            if (char.isLowerCase()) char.titlecase() else char.toString()
        }
    }

    private fun String.toCompactTimestampLabel(): String {
        val trimmed = trim()
        if (trimmed.isBlank()) return "recently"

        val datePart = trimmed.takeIf { it.length >= 10 && it[4] == '-' && it[7] == '-' }?.substring(0, 10)
        val timePart = trimmed
            .substringAfter('T', missingDelimiterValue = "")
            .take(5)
            .takeIf { it.length == 5 && it[2] == ':' }

        val dateLabel = datePart?.toCompactDateLabel()
            ?: trimmed.take(16).ifBlank { "recently" }

        return if (timePart.isNullOrBlank()) {
            dateLabel
        } else {
            "$dateLabel, $timePart"
        }
    }

    private fun String.toCompactDateLabel(): String {
        val parts = split("-")
        if (parts.size != 3) return trim().ifBlank { "recently" }
        val month = parts[1].toIntOrNull() ?: return trim().ifBlank { "recently" }
        val day = parts[2].toIntOrNull() ?: return trim().ifBlank { "recently" }
        return "${day.toString().padStart(2, '0')} ${shortMonthName(month)}"
    }

    private fun JsonObject.toHomeUserProfile(): HomeUserProfileUiState {
        val fullName = firstNonBlankString(
            "full_name",
            "name",
            "display_name",
            "fullName",
            "displayName"
        ).ifBlank {
            listOf(
                firstNonBlankString("first_name", "firstName"),
                firstNonBlankString("middle_name", "middleName"),
                firstNonBlankString("last_name", "lastName")
            ).filter { it.isNotBlank() }.joinToString(" ")
        }
        val firstName = firstNonBlankString("first_name", "firstName").ifBlank {
            fullName.trim().split(Regex("\\s+")).firstOrNull().orEmpty()
        }
        val lastName = firstNonBlankString("last_name", "lastName").ifBlank {
            fullName.trim().split(Regex("\\s+")).lastOrNull().orEmpty()
        }
        val middleName = firstNonBlankString("middle_name", "middleName")
        return HomeUserProfileUiState(
            fullName = fullName,
            firstName = firstName,
            middleName = middleName,
            lastName = lastName,
            mobileNumber = firstNonBlankString("mobile", "phone", "phone_number", "mobile_number"),
            email = firstNonBlankString("email", "email_address", "mail"),
            employeeId = firstNonBlankString("employee_id", "staff_id", "employeeId", "code"),
            lastLoginLabel = firstNonBlankString("last_login_at", "last_used_at", "lastLoginAt", "lastLogin")
                .takeIf { it.isNotBlank() }
                ?.toCompactTimestampLabel()
                .orEmpty(),
            mobileVerified = firstNonBlankBoolean("mobile_verified", "is_mobile_verified", "mobileVerified"),
            emailVerified = firstNonBlankBoolean("email_verified", "is_email_verified", "emailVerified", "verified"),
            profileImageUrl = firstNonBlankString("profile_image_url", "profileImageUrl"),
            pushNotificationsEnabled = firstNonBlankBoolean("push_notification_enabled", "pushNotificationsEnabled") ?: true,
            emailNotificationsEnabled = firstNonBlankBoolean("email_notification_enabled", "emailNotificationsEnabled") ?: true,
            isLoading = false
        )
    }

    private fun SchoolStaffResponse.toHomeUserProfile(): HomeUserProfileUiState {
        val fullName = name.orEmpty().trim()
        val nameParts = fullName.split(Regex("\\s+")).filter { it.isNotBlank() }
        val custom = customAttributes.orEmpty()
        fun customString(vararg keys: String): String = keys.firstNotNullOfOrNull { key ->
            custom[key]?.jsonPrimitive?.contentOrNull?.trim()?.takeIf { it.isNotBlank() }
        }.orEmpty()
        return HomeUserProfileUiState(
            fullName = fullName,
            firstName = customString("first_name", "firstName").ifBlank { nameParts.firstOrNull().orEmpty() },
            middleName = customString("middle_name", "middleName"),
            lastName = customString("last_name", "lastName").ifBlank { nameParts.drop(1).joinToString(" ") },
            mobileNumber = resolvedMobile,
            email = email.orEmpty().trim().ifBlank { customString("email", "email_address", "mail") },
            employeeId = employeeId.orEmpty().trim(),
            schoolRole = roleTitle.orEmpty().trim(),
            authRole = authRole.orEmpty().trim(),
            mobileVerified = custom["mobile_verified"]?.jsonPrimitive?.booleanOrNull,
            emailVerified = custom["email_verified"]?.jsonPrimitive?.booleanOrNull,
            profileImageUrl = profileImageUrl.orEmpty(),
            pushNotificationsEnabled = pushNotificationEnabled ?: true,
            emailNotificationsEnabled = emailNotificationEnabled ?: true,
            isLoading = false
        )
    }

    private fun ParentResponse.toHomeUserProfile(): HomeUserProfileUiState {
        val fullName = name.orEmpty().trim()
        val nameParts = fullName.split(Regex("\\s+")).filter { it.isNotBlank() }
        val custom = customAttributes.orEmpty()
        fun customString(vararg keys: String): String = keys.firstNotNullOfOrNull { key ->
            custom[key]?.jsonPrimitive?.contentOrNull?.trim()?.takeIf { it.isNotBlank() }
        }.orEmpty()
        return HomeUserProfileUiState(
            fullName = fullName,
            firstName = customString("first_name", "firstName").ifBlank { nameParts.firstOrNull().orEmpty() },
            middleName = customString("middle_name", "middleName"),
            lastName = customString("last_name", "lastName").ifBlank { nameParts.drop(1).joinToString(" ") },
            mobileNumber = mobile.orEmpty().trim()
                .ifBlank { customString("mobile", "mobile_number", "phone", "phone_number") },
            email = email.orEmpty().trim()
                .ifBlank { customString("email", "email_address", "mail") },
            mobileVerified = mobileVerified ?: custom["mobile_verified"]?.jsonPrimitive?.booleanOrNull,
            emailVerified = emailVerified ?: custom["email_verified"]?.jsonPrimitive?.booleanOrNull,
            profileImageUrl = profileImageUrl.orEmpty(),
            pushNotificationsEnabled = pushNotificationEnabled ?: true,
            emailNotificationsEnabled = emailNotificationEnabled ?: true,
            isLoading = false
        )
    }

    private fun JsonObject.firstNonBlankString(vararg keys: String): String {
        for (key in keys) {
            val value = valueForKey(key)
            if (value.isNotBlank()) return value
        }
        return ""
    }

    private fun JsonObject.firstNonBlankBoolean(vararg keys: String): Boolean? {
        for (key in keys) {
            val value = booleanForKey(key)
            if (value != null) return value
        }
        return null
    }

    private fun JsonObject.valueForKey(key: String): String {
        val direct = this[key]?.jsonPrimitive?.contentOrNull.orEmpty()
        if (direct.isNotBlank()) return direct
        val nested = nestedValue(key)
        return nested.orEmpty()
    }

    private fun JsonObject.booleanForKey(key: String): Boolean? {
        this[key]?.jsonPrimitive?.booleanOrNull?.let { return it }
        val asString = this[key]?.jsonPrimitive?.contentOrNull?.trim().orEmpty().lowercase()
        if (asString in setOf("true", "1", "yes", "y")) return true
        if (asString in setOf("false", "0", "no", "n")) return false
        val nested = nestedBoolean(key)
        return nested
    }

    private fun JsonObject.nestedValue(key: String): String? {
        val containers = listOf("profile", "user", "data", "attributes", "custom_attributes")
        for (containerKey in containers) {
            val nestedObject = this[containerKey]?.jsonObject ?: continue
            val nestedValue = nestedObject[key]?.jsonPrimitive?.contentOrNull.orEmpty()
            if (nestedValue.isNotBlank()) return nestedValue
        }
        return null
    }

    private fun JsonObject.nestedBoolean(key: String): Boolean? {
        val containers = listOf("profile", "user", "data", "attributes", "custom_attributes")
        for (containerKey in containers) {
            val nestedObject = this[containerKey]?.jsonObject ?: continue
            val nestedPrimitive = nestedObject[key]?.jsonPrimitive ?: continue
            nestedPrimitive.booleanOrNull?.let { return it }
            val nestedString = nestedPrimitive.contentOrNull?.trim().orEmpty().lowercase()
            if (nestedString in setOf("true", "1", "yes", "y")) return true
            if (nestedString in setOf("false", "0", "no", "n")) return false
        }
        return null
    }

    private fun shortMonthName(month: Int): String = when (month) {
        1 -> "JAN"
        2 -> "FEB"
        3 -> "MAR"
        4 -> "APR"
        5 -> "MAY"
        6 -> "JUN"
        7 -> "JUL"
        8 -> "AUG"
        9 -> "SEP"
        10 -> "OCT"
        11 -> "NOV"
        12 -> "DEC"
        else -> "UNK"
    }

    private fun loadDashboardAnalyticsIfNeeded(roleCode: String, forceRemote: Boolean = false) {
        if (!supportsPlatformDashboard(roleCode)) return

        if (!forceRemote && _uiState.value.dashboardAnalytics != null) {
            scope.launch {
                HomeDashboardRepository.loadDashboardAnalytics(roleCode).onSuccess { response ->
                    _uiState.update { current -> current.copy(dashboardAnalytics = response) }
                }
            }
            return
        }
        markDashboardMetricsLoading(delta = 1)
        scope.launch {
            try {
                val result = HomeDashboardRepository.loadDashboardAnalytics(roleCode, forceRemote = forceRemote)
                result.fold(
                    onSuccess = { response ->
                        _uiState.update { current ->
                            current.copy(dashboardAnalytics = response)
                        }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadDashboardAnalytics failure role=$roleCode", throwable)
                    }
                )
            } finally {
                markDashboardMetricsLoading(delta = -1)
            }
        }
    }

    private fun loadSchoolsAnalyticsIfNeeded(roleCode: String, forceRemote: Boolean = false) {
        if (!supportsSchoolsDashboard(roleCode)) return

        if (!forceRemote && _uiState.value.dashboardSchoolsAnalytics != null) {
            scope.launch {
                HomeDashboardRepository.loadDashboardSchoolsAnalytics(roleCode).onSuccess { response ->
                    _uiState.update { current -> current.copy(dashboardSchoolsAnalytics = response) }
                }
            }
            return
        }
        markDashboardMetricsLoading(delta = 1)
        scope.launch {
            try {
                val result = HomeDashboardRepository.loadDashboardSchoolsAnalytics(roleCode, forceRemote = forceRemote)
                result.fold(
                    onSuccess = { response ->
                        _uiState.update { current ->
                            current.copy(dashboardSchoolsAnalytics = response)
                        }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadSchoolsAnalytics failure role=$roleCode", throwable)
                    }
                )
            } finally {
                markDashboardMetricsLoading(delta = -1)
            }
        }
    }

    private fun loadDashboardStatisticsIfNeeded(roleCode: String, forceRemote: Boolean = false) {
        if (!supportsPlatformDashboard(roleCode)) return

        if (!forceRemote && _uiState.value.dashboardStatistics != null) {
            scope.launch {
                HomeDashboardRepository.loadDashboardStatistics(roleCode).onSuccess { response ->
                    _uiState.update { current -> current.copy(dashboardStatistics = response) }
                }
            }
            return
        }
        markDashboardMetricsLoading(delta = 1)
        scope.launch {
            try {
                val result = HomeDashboardRepository.loadDashboardStatistics(roleCode, forceRemote = forceRemote)
                result.fold(
                    onSuccess = { response ->
                        _uiState.update { current ->
                            current.copy(dashboardStatistics = response)
                        }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadDashboardStatistics failure role=$roleCode", throwable)
                    }
                )
            } finally {
                markDashboardMetricsLoading(delta = -1)
            }
        }
    }

    private fun loadRecentActivitiesIfNeeded(roleCode: String, forceRemote: Boolean = false) {
        if (!supportsPlatformDashboard(roleCode)) return

        if (!forceRemote && _uiState.value.recentActivities != null) {
            scope.launch {
                HomeDashboardRepository.loadRecentActivities(roleCode).onSuccess { response ->
                    _uiState.update { current -> current.copy(recentActivities = response) }
                }
            }
            return
        }
        markDashboardFeedLoading(delta = 1)
        scope.launch {
            try {
                val result = HomeDashboardRepository.loadRecentActivities(
                    roleCode,
                    forceRemote = forceRemote
                )
                result.fold(
                    onSuccess = { response ->
                        _uiState.update { current ->
                            current.copy(recentActivities = response)
                        }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadRecentActivities failure role=$roleCode", throwable)
                    }
                )
            } finally {
                markDashboardFeedLoading(delta = -1)
            }
        }
    }

    private fun deferSystemHealthLoad(roleCode: String, forceRemote: Boolean = false) {
        if (!supportsPlatformDashboard(roleCode)) return
        scope.launch {
            // Yield so metrics / activity requests start first.
            delay(500)
            loadSystemHealthIfNeeded(roleCode, forceRemote = forceRemote)
        }
    }

    private fun loadSystemHealthIfNeeded(roleCode: String, forceRemote: Boolean = false) {
        if (!supportsPlatformDashboard(roleCode)) return

        if (!forceRemote && _uiState.value.systemHealth != null) {
            scope.launch {
                HomeDashboardRepository.loadSystemHealth(roleCode).onSuccess { response ->
                    _uiState.update { current -> current.copy(systemHealth = response) }
                }
            }
            return
        }
        // Do not gate the activity panel on health — load quietly in the background.
        scope.launch {
            HomeDashboardRepository.loadSystemHealth(roleCode, forceRemote = forceRemote).fold(
                onSuccess = { response ->
                    _uiState.update { current -> current.copy(systemHealth = response) }
                },
                onFailure = { throwable ->
                    scLog("$TAG loadSystemHealth failure role=$roleCode", throwable)
                }
            )
        }
    }

    private fun loadSchoolAnalyticsIfNeeded(
        roleCode: String,
        schoolId: String,
        schoolCode: String,
        forceRemote: Boolean = false
    ) {
        if (!supportsSchoolDashboard(roleCode)) return

        val normalizedSchoolCode = schoolCode.trim()
        if (normalizedSchoolCode.isBlank()) {
            if (schoolId.trim().isBlank()) {
                scLog("$TAG loadSchoolAnalytics skipped role=$roleCode school=<blank>")
                return
            }
        }

        if (!forceRemote && _uiState.value.schoolDashboardAnalytics != null && normalizedSchoolCode.isNotBlank()) {
            scope.launch {
                HomeDashboardRepository.loadSchoolAnalytics(normalizedSchoolCode)
            }
            return
        }

        _uiState.update { current ->
            current.copy(isSchoolDashboardLoading = true)
        }
        scope.launch {
            try {
                if (normalizedSchoolCode.isBlank() && roleCode in setOf(
                        SchoolKeys.V_ROLE_SCHOOL_ADMIN,
                        SchoolKeys.V_ROLE_TEACHER,
                        SchoolKeys.V_ROLE_PARENT,
                        SchoolKeys.V_ROLE_SUPPORT_STAFF
                    )
                ) {
                    scLog("$TAG loadSchoolAnalytics skipped tenant role without schoolCode schoolId=${schoolId.ifBlank { "<blank>" }}")
                    return@launch
                }
                val resolvedSchoolCode = when {
                    normalizedSchoolCode.isNotBlank() -> normalizedSchoolCode
                    schoolId.isNotBlank() -> {
                        val resolved = SchoolRepository.resolveSchoolCodeById(schoolId).getOrElse { throw it }
                        _uiState.update { current ->
                            current.copy(schoolCode = resolved)
                        }
                        UserSessionStore.setHomeRoleSchoolCode(resolved)
                        resolved
                    }
                    else -> ""
                }
                if (resolvedSchoolCode.isBlank()) {
                    scLog("$TAG loadSchoolAnalytics skipped resolve role=$roleCode schoolId=${schoolId.ifBlank { "<blank>" }}")
                    return@launch
                }
                val result = HomeDashboardRepository.loadSchoolAnalytics(
                    resolvedSchoolCode,
                    forceRemote = forceRemote
                )
                result.fold(
                    onSuccess = { response ->
                        _uiState.update { current ->
                            current.copy(
                                schoolDashboardAnalytics = response,
                                schoolLabel = response.school.name.ifBlank { current.schoolLabel },
                                schoolCode = response.school.code.ifBlank { resolvedSchoolCode }
                            )
                        }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadSchoolAnalytics failure role=$roleCode school=$resolvedSchoolCode", throwable)
                    }
                )
            } catch (throwable: Throwable) {
                // Dashboard enrichment must not bring down the authenticated shell.
                // In particular, school roles may not use the platform school-list
                // endpoint when their persisted school code is unavailable.
                scLog("$TAG loadSchoolAnalytics exception role=$roleCode schoolId=${schoolId.ifBlank { "<blank>" }}", throwable)
            } finally {
                _uiState.update { current ->
                    current.copy(isSchoolDashboardLoading = false)
                }
            }
        }
    }

    private fun loadSchoolStatisticsIfNeeded(
        roleCode: String,
        schoolId: String,
        forceRemote: Boolean = false
    ) {
        if (!supportsSchoolDashboard(roleCode)) return

        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            scLog("$TAG loadSchoolStatistics skipped role=$roleCode schoolId=<blank>")
            return
        }

        if (!forceRemote && _uiState.value.schoolStatistics != null) {
            scope.launch {
                SchoolStatisticsRepository.loadSchoolStatistics(normalizedSchoolId)
            }
            return
        }

        _uiState.update { current ->
            current.copy(isSchoolStatisticsLoading = true)
        }
        scope.launch {
            try {
                val result = SchoolStatisticsRepository.loadSchoolStatistics(
                    normalizedSchoolId,
                    forceRemote = forceRemote
                )
                result.fold(
                    onSuccess = { response ->
                        _uiState.update { current ->
                            current.copy(
                                schoolStatistics = response
                            )
                        }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadSchoolStatistics failure role=$roleCode school=$normalizedSchoolId", throwable)
                    }
                )
            } finally {
                _uiState.update { current ->
                    current.copy(isSchoolStatisticsLoading = false)
                }
            }
        }
    }

    private fun loadSchoolActiveAcademicYearIfNeeded(roleCode: String, schoolId: String, schoolCode: String = "") {
        if (!supportsSchoolDashboard(roleCode)) return

        val requestedSchoolId = schoolId.trim()
        val requestedSchoolCode = schoolCode.trim()
        if (requestedSchoolId.isBlank() && requestedSchoolCode.isBlank()) {
            scLog("$TAG loadSchoolActiveAcademicYear skipped role=$roleCode schoolId=<blank> schoolCode=<blank>")
            return
        }

        _uiState.update { current ->
            current.copy(isActiveAcademicYearLoading = true)
        }
        scope.launch {
            try {
                val normalizedSchoolId = requestedSchoolId.ifBlank {
                    SchoolRepository.loadSchoolByCode(requestedSchoolCode)
                        .getOrElse { throw it }
                        .id
                        .orEmpty()
                        .trim()
                        .ifBlank { error("School lookup returned no school_id for code=$requestedSchoolCode") }
                }
                scLog("$TAG loadSchoolActiveAcademicYear start role=$roleCode schoolId=$normalizedSchoolId")
                val selectedYear = AcademicYearRepository.loadCurrentAcademicYear(normalizedSchoolId)
                    .getOrElse { throw it }

                _uiState.update { current ->
                    current.copy(
                        activeAcademicYearId = selectedYear?.id.orEmpty(),
                        activeAcademicYearLabel = selectedYear?.label.orEmpty(),
                        isActiveAcademicYearLoading = false
                    )
                }
                scLog(
                    "$TAG loadSchoolActiveAcademicYear complete role=$roleCode schoolId=$normalizedSchoolId academicYear=${selectedYear?.id.orEmpty().ifBlank { "<blank>" }} label=${selectedYear?.label.orEmpty().ifBlank { "<blank>" }}"
                )
            } catch (throwable: Throwable) {
                scLog("$TAG loadSchoolActiveAcademicYear failure role=$roleCode schoolId=$normalizedSchoolId", throwable)
                _uiState.update { current ->
                    current.copy(
                        activeAcademicYearId = "",
                        activeAcademicYearLabel = "",
                        isActiveAcademicYearLoading = false
                    )
                }
            }
        }
    }

    private fun loadSchoolEventsNoticesIfNeeded(
        roleCode: String,
        schoolId: String,
        forceRemote: Boolean = false
    ) {
        if (!supportsSchoolDashboard(roleCode)) return

        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            scLog("$TAG loadSchoolEventsNotices skipped role=$roleCode schoolId=<blank>")
            return
        }

        if (!forceRemote && _uiState.value.schoolDashboardEventsNotices != null) {
            scope.launch {
                HomeDashboardRepository.loadSchoolEventsNotices(normalizedSchoolId)
            }
            return
        }

        _uiState.update { current ->
            current.copy(isSchoolDashboardEventsNoticesLoading = true)
        }
        scope.launch {
            try {
                val result = HomeDashboardRepository.loadSchoolEventsNotices(
                    normalizedSchoolId,
                    forceRemote = forceRemote
                )
                result.fold(
                    onSuccess = { response ->
                        _uiState.update { current ->
                            current.copy(
                                schoolDashboardEventsNotices = response
                            )
                        }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadSchoolEventsNotices failure role=$roleCode schoolId=$normalizedSchoolId", throwable)
                    }
                )
            } finally {
                _uiState.update { current ->
                    current.copy(isSchoolDashboardEventsNoticesLoading = false)
                }
            }
        }
    }

    private fun loadTeacherDashboardIfNeeded(roleCode: String, forceRemote: Boolean = false) {
        if (roleCode != SchoolKeys.V_ROLE_TEACHER) return
        if (_uiState.value.teacherDashboard != null && !forceRemote) {
            scope.launch { HomeDashboardRepository.loadTeacherDashboard(roleCode) }
            return
        }
        _uiState.update { it.copy(isTeacherDashboardLoading = true) }
        scope.launch {
            try {
                HomeDashboardRepository.loadTeacherDashboard(roleCode, forceRemote = forceRemote).fold(
                    onSuccess = { response ->
                        _uiState.update { it.copy(teacherDashboard = response) }
                    },
                    onFailure = { throwable ->
                        scLog("$TAG loadTeacherDashboard failure role=$roleCode", throwable)
                        _uiState.update { it.copy(teacherDashboard = TeacherDashboardOverviewResponse()) }
                    }
                )
            } finally {
                _uiState.update { it.copy(isTeacherDashboardLoading = false) }
            }
        }
    }

    /** Refresh dashboard data when navigation returns to Dashboard. */
    private fun refreshDashboard(roleCode: String) {
        if (roleCode == SchoolKeys.V_ROLE_PARENT) {
            loadParentHomeIfNeeded(forceRemote = true)
            loadActiveAnnouncementsIfNeeded(normalizedSchoolId, forceRemote = true)
            return
        }
        loadDashboardAnalyticsIfNeeded(roleCode, forceRemote = true)
        loadSchoolsAnalyticsIfNeeded(roleCode, forceRemote = true)
        loadDashboardStatisticsIfNeeded(roleCode, forceRemote = true)
        loadRecentActivitiesIfNeeded(roleCode, forceRemote = true)
        deferSystemHealthLoad(roleCode, forceRemote = true)
        loadTeacherDashboardIfNeeded(roleCode, forceRemote = true)
        loadSchoolAnalyticsIfNeeded(
            roleCode,
            normalizedSchoolId,
            normalizedSchoolCode,
            forceRemote = true
        )
        loadSchoolStatisticsIfNeeded(roleCode, normalizedSchoolId, forceRemote = true)
        loadSchoolActiveAcademicYearIfNeeded(roleCode, normalizedSchoolId, normalizedSchoolCode)
        loadSchoolEventsNoticesIfNeeded(roleCode, normalizedSchoolId, forceRemote = true)
    }

    private fun loadParentHomeIfNeeded(forceRemote: Boolean = false) {
        if (authenticatedUserId.isBlank()) {
            _uiState.update { it.copy(isParentDashboardLoading = false, isParentProfileLoading = false) }
            return
        }
        scope.launch {
            HomeDashboardRepository.loadParentDashboard(
                authenticatedUserId,
                normalizedSchoolId,
                forceRemote = forceRemote
            ).fold(
                onSuccess = { response ->
                    _uiState.update { it.copy(parentDashboard = response) }
                    val resolvedParentId = response.parentId.orEmpty().trim()
                    if (resolvedParentId.isBlank()) {
                        scLog("$TAG loadParentProfile skipped: parent dashboard did not return parentId")
                    } else {
                        HomeDashboardRepository.loadParentRecord(resolvedParentId).fold(
                            onSuccess = { parent ->
                                val profile = parent.toHomeUserProfile()
                                ShellProfileStore.update(
                                    fullName = profile.fullName,
                                    email = profile.email,
                                    profileImageUrl = profile.profileImageUrl,
                                    pushNotificationsEnabled = profile.pushNotificationsEnabled,
                                    emailNotificationsEnabled = profile.emailNotificationsEnabled,
                                    mobileNumber = profile.mobileNumber,
                                    employeeId = "",
                                    schoolRole = "",
                                    authRole = "",
                                    mobileVerified = profile.mobileVerified,
                                    emailVerified = profile.emailVerified
                                )
                                _uiState.update { current -> current.copy(userProfile = profile) }
                            },
                            onFailure = { throwable ->
                                scLog("$TAG loadParentRecord failure parent=$resolvedParentId", throwable)
                                _uiState.update { current -> current.copy(userProfile = current.userProfile.copy(isLoading = false)) }
                            }
                        )
                        HomeDashboardRepository.loadParentProfile(resolvedParentId).fold(
                            onSuccess = { profile ->
                                _uiState.update { current ->
                                    val currentProfile = current.userProfile
                                    current.copy(
                                        parentProfile = profile,
                                        userProfile = currentProfile.copy(
                                            mobileNumber = currentProfile.mobileNumber.ifBlank { profile.primaryContactPhone.orEmpty() },
                                            email = currentProfile.email.ifBlank { profile.primaryContactEmail.orEmpty() }
                                        )
                                    )
                                }
                            },
                            onFailure = { throwable -> scLog("$TAG loadParentProfile failure", throwable) }
                        )
                        HomeDashboardRepository.loadParentWards(resolvedParentId).fold(
                            onSuccess = { wards -> _uiState.update { current -> current.copy(parentWards = wards) } },
                            onFailure = { throwable -> scLog("$TAG loadParentWards failure parent=$resolvedParentId", throwable) }
                        )
                    }
                },
                onFailure = { throwable -> scLog("$TAG loadParentDashboard failure", throwable) }
            )
            _uiState.update { it.copy(isParentDashboardLoading = false) }
            _uiState.update { it.copy(isParentProfileLoading = false) }
        }
    }

    private fun markDashboardMetricsLoading(delta: Int) {
        val requestCount = updateAtomicRequestCount(dashboardMetricsRequestsInFlight, delta)
        _uiState.update { current ->
            current.copy(isDashboardLoading = requestCount > 0)
        }
    }

    private fun markDashboardFeedLoading(delta: Int) {
        val requestCount = updateAtomicRequestCount(dashboardFeedRequestsInFlight, delta)
        _uiState.update { current ->
            current.copy(isDashboardFeedLoading = requestCount > 0)
        }
    }

    private fun updateAtomicRequestCount(counter: AtomicInt, delta: Int): Int {
        while (true) {
            val current = counter.value
            val next = (current + delta).coerceAtLeast(0)
            if (counter.compareAndSet(current, next)) {
                return next
            }
        }
    }

    private fun supportsPlatformDashboard(roleCode: String): Boolean {
        return when (roleCode) {
            SchoolKeys.V_ROLE_APP_SUPER_ADMIN,
            SchoolKeys.V_ROLE_APP_ADMIN,
            SchoolKeys.V_ROLE_APP_BACKEND -> true
            else -> false
        }
    }

    private fun supportsSchoolsDashboard(roleCode: String): Boolean {
        return roleCode == SchoolKeys.V_ROLE_APP_SUPER_ADMIN
    }

    private fun supportsSchoolDashboard(roleCode: String): Boolean {
        return roleCode == SchoolKeys.V_ROLE_SCHOOL_ADMIN
    }

    fun clear() {
        scope.cancel()
    }
}
